/**
 * Nuxt Manual Router.js
 *
 * @since 2018-08-22
 * @author mk.ko
 *
 * Nuxt 라우터 수동 매핑
 * ++ : 마크 의미, 실제 파일경로와 import 명칭, URI 가 틀림 표기
 */

import Vue from 'vue'
import Router from 'vue-router'
import { normalizeURL, decode } from 'ufo'
import { interopDefault } from './utils'
import scrollBehavior from './router.scrollBehavior.js'

/* =============================================
  * 공통
  * ============================================= */

// Index
const index = () => interopDefault(import('~/pages/index.vue' /* webpackChunkName: "pages/index" */))

// LoginDev
const loginDev = () => interopDefault(import('~/pages/loginDev.vue' /* webpackChunkName: "pages/loginDev" */))

/* =============================================
  * Dashboard
  * ============================================= */
const dashboardParent = () => interopDefault(import('~/pages/dashboard.vue' /* webpackChunkName: "pages/dashboard" */))
// * 대시보드
const dashboard = () => interopDefault(import('~/pages/dashboard/dashboard/dashboard.vue' /* webpackChunkName: "pages/dashboard/dashboard/dashboard" */))
// * 국가별 정보
const countryInfo = () => interopDefault(import('~/pages/dashboard/dashboard/countryInfo.vue' /* webpackChunkName: "pages/dashboard/dashboard/countryInfo" */))
// * 이벤트 모니터링
const deviceStatus = () => interopDefault(import('~/pages/dashboard/dashboard/deviceStatus.vue' /* webpackChunkName: "pages/dashboard/dashboard/deviceStatus" */))
// * 토폴로지
const topology = () => interopDefault(import('~/pages/dashboard/dashboard/topology.vue' /* webpackChunkName: "pages/dashboard/dashboard/topology" */))

/* =============================================
  * System
  * ============================================= */
const systemParent = () => interopDefault(import('~/pages/system.vue' /* webpackChunkName: "pages/system" */))
/** 장비 관리 */
const deviceCompositionStatus = () => interopDefault(import('~/pages/system/deviceConfig/deviceCompositionStatus.vue' /* webpackChunkName: "pages/system/deviceConfig/deviceCompositionStatus" */))
const deviceGroupSettings = () => interopDefault(import('~/pages/system/deviceConfig/deviceGroupSettings.vue' /* webpackChunkName: "pages/system/deviceConfig/deviceGroupSettings" */))
const deviceSearch = () => interopDefault(import('~/pages/system/deviceConfig/deviceSearch.vue' /* webpackChunkName: "pages/system/deviceConfig/deviceSearch" */))
const deviceZtpToken = () => interopDefault(import('~/pages/system/deviceConfig/deviceZtpToken.vue' /* webpackChunkName: "pages/system/deviceConfig/deviceZtpToken" */))
const wipsDeviceLink = () => interopDefault(import('~/pages/system/deviceConfig/wipsDeviceLink.vue' /* webpackChunkName: "pages/system/deviceConfig/wipsDeviceLink" */))
/** 관리자 설정 */
const adminGeneralSettings = () => interopDefault(import('~/pages/system/adminSettings/adminGeneralSettings.vue' /* webpackChunkName: "pages/system/adminSettings/adminGeneralSettings" */))
const administrator = () => interopDefault(import('~/pages/system/adminSettings/administrator.vue' /* webpackChunkName: "pages/system/adminSettings/administrator" */))
const adminGroupSettings = () => interopDefault(import('~/pages/system/adminSettings/adminGroupSettings.vue' /* webpackChunkName: "pages/system/adminSettings/adminGroupSettings" */))
const adminIpSettings = () => interopDefault(import('~/pages/system/adminSettings/adminIpSettings.vue' /* webpackChunkName: "pages/system/adminSettings/adminIpSettings" */))
const adminDeviceGroupSettings = () => interopDefault(import('~/pages/system/adminSettings/adminDeviceGroupSettings.vue' /* webpackChunkName: "pages/system/adminSettings/adminDeviceGroupSettings" */))
const externalAuthSettings = () => interopDefault(import('~/pages/system/adminSettings/externalAuthSettings.vue' /* webpackChunkName: "pages/system/adminSettings/externalAuthSettings" */))
/** 모니터링 설정 */
const eventGeneralSettings = () => interopDefault(import('~/pages/system/monitoringSettings/eventGeneralSettings.vue' /* webpackChunkName: "pages/system/monitoringSettings/eventGeneralSettings" */))
const eventRuleSettings = () => interopDefault(import('~/pages/system/monitoringSettings/eventRuleSettings.vue' /* webpackChunkName: "pages/system/monitoringSettings/eventRuleSettings" */))
/** 티켓 설정 */
const ticketSettings = () => interopDefault(import('~/pages/system/monitoringSettings/ticketMgmt/ticketSettings.vue' /* webpackChunkName: "pages/system/monitoringSettings/ticketMgmt/ticketSettings" */))
const ticketGroupSettings = () => interopDefault(import('~/pages/system/monitoringSettings/ticketMgmt/ticketGroupSettings.vue' /* webpackChunkName: "pages/system/monitoringSettings/ticketMgmt/ticketGroupSettings" */))
/** 외부 연동 서버 */
const smtpSettings = () => interopDefault(import('~/pages/system/externalServer/smtpSettings.vue' /* webpackChunkName: "pages/system/externalServer/smtpSettings" */))
const signatureSettings = () => interopDefault(import('~/pages/system/externalServer/signatureSettings.vue' /* webpackChunkName: "pages/system/externalServer/signatureSettings" */))
const signatureDetails = () => interopDefault(import('~/pages/system/externalServer/signatureDetails.vue' /* webpackChunkName: "pages/system/externalServer/signatureDetails" */))
const systemSnmpSettings = () => interopDefault(import('~/pages/system/externalServer/systemSnmpSettings.vue' /* webpackChunkName: "pages/system/externalServer/systemSnmpSettings" */))
const smsSettings = () => interopDefault(import('~/pages/system/externalServer/smsSettings.vue' /* webpackChunkName: "pages/system/externalServer/smsSettings" */))
const tamsSyslogSettings = () => interopDefault(import('~/pages/system/externalServer/tamsSyslogSettings.vue' /* webpackChunkName: "pages/system/externalServer/tamsSyslogSettings" */))
const tamsLogSendSettings = () => interopDefault(import('~/pages/system/externalServer/tamsLogSendSettings.vue' /* webpackChunkName: "pages/system/externalServer/tamsLogSendSettings" */))
/** 시스템 구성 */
const systemSettings = () => interopDefault(import('~/pages/system/systemConfig/systemSettings.vue' /* webpackChunkName: "pages/system/systemConfig/systemSettings" */))
const networkSettings = () => interopDefault(import('~/pages/system/systemConfig/networkSettings.vue' /* webpackChunkName: "pages/system/systemConfig/networkSettings" */))
const systemBackupSchedule = () => interopDefault(import('~/pages/system/systemConfig/systemBackupSchedule.vue' /* webpackChunkName: "pages/system/systemConfig/systemBackupSchedule" */))
const systemBackup = () => interopDefault(import('~/pages/system/systemConfig/systemBackup.vue' /* webpackChunkName: "pages/system/systemConfig/systemBackup" */))
const dockerMgmt = () => interopDefault(import('~/pages/system/systemConfig/dockerMgmt.vue' /* webpackChunkName: "pages/system/systemConfig/dockerMgmt" */))
const licenseMgmt = () => interopDefault(import('~/pages/system/systemConfig/licenseMgmt.vue' /* webpackChunkName: "pages/system/systemConfig/licenseMgmt" */))
const dnsSettings = () => interopDefault(import('~/pages/system/systemConfig/dnsSettings.vue' /* webpackChunkName: "pages/system/systemConfig/dnsSettings" */))
const clusterSettings = () => interopDefault(import('~/pages/system/systemConfig/clusterSettings.vue' /* webpackChunkName: "pages/system/systemConfig/clusterSettings" */))

/** 패치 관리 ++ */
const prismPatch = () => interopDefault(import('~/pages/system/patch/prismPatch.vue' /* webpackChunkName: "pages/system/patch/prismPatch" */))
const devSoftwarePatch = () => interopDefault(import('~/pages/system/patch/devicePatch/softwarePatch.vue' /* webpackChunkName: "pages/system/patch/devicePatch/softwarePatch" */))
const devLicensePatch = () => interopDefault(import('~/pages/system/patch/devicePatch/deviceLicenseMgmt.vue' /* webpackChunkName: "pages/system/patch/devicePatch/deviceLicenseMgmt" */))
const devAgentPatch = () => interopDefault(import('~/pages/system/patch/devicePatch/agentPatch.vue' /* webpackChunkName: "pages/system/patch/devicePatch/agentPatch" */))
const devApplicationPatch = () => interopDefault(import('~/pages/system/patch/devicePatch/applicationDBPatch.vue' /* webpackChunkName: "pages/system/patch/devicePatch/applicationDBPatch" */))
const antiVirusDB = () => interopDefault(import('~/pages/system/patch/devicePatch/antiVirusDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/antiVirusDB" */))
const dlpDB = () => interopDefault(import('~/pages/system/patch/devicePatch/dlpDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/dlpDB" */))
const fileTypeCtrlDB = () => interopDefault(import('~/pages/system/patch/devicePatch/fileTypeCtrlDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/fileTypeCtrlDB" */))
const ipsSignatureDB = () => interopDefault(import('~/pages/system/patch/devicePatch/ipsSignatureDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/ipsSignatureDB" */))
const ngipsSignatureDB = () => interopDefault(import('~/pages/system/patch/devicePatch/ngipsSignatureDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/ngipsSignatureDB" */))
const adsIpsSignatureDB = () => interopDefault(import('~/pages/system/patch/devicePatch/adsIpsSignatureDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/adsIpsSignatureDB" */))
const aptPolicy = () => interopDefault(import('~/pages/system/patch/devicePatch/aptPolicy.vue' /* webpackChunkName: "pages/system/patch/devicePatch/aptPolicy" */))
const webFilterDB = () => interopDefault(import('~/pages/system/patch/devicePatch/webFilterDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/webFilterDB" */))
const blackListDB = () => interopDefault(import('~/pages/system/patch/devicePatch/blackListDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/blackListDB" */))
const geoIpDB = () => interopDefault(import('~/pages/system/patch/devicePatch/geoIpDB.vue' /* webpackChunkName: "pages/system/patch/devicePatch/geoIpDB" */))
/** Tools */
const ping = () => interopDefault(import('~/pages/system/tools/ping.vue' /* webpackChunkName: "pages/system/tools/ping" */))
const traceRoute = () => interopDefault(import('~/pages/system/tools/traceRoute.vue' /* webpackChunkName: "pages/system/tools/traceRoute" */))
const whois = () => interopDefault(import('~/pages/system/tools/whois.vue' /* webpackChunkName: "pages/system/tools/whois" */))
const snmp = () => interopDefault(import('~/pages/system/tools/snmp.vue' /* webpackChunkName: "pages/system/tools/snmp" */))
const terminalAccess = () => interopDefault(import('~/pages/system/tools/terminalAccess.vue' /* webpackChunkName: "pages/system/tools/terminalAccess" */))
const integrityCheck = () => interopDefault(import('~/pages/system/tools/integrityCheck.vue' /* webpackChunkName: "pages/system/tools/integrityCheck" */))
/** 동기화 현황 ++ */
const synchronizationStatus = () => interopDefault(import('~/pages/system/synchronizationStatus/synchronizationStatus.vue' /* webpackChunkName: "pages/system/synchronizationStatus/synchronizationStatus" */))
const syncSettings = () => interopDefault(import('~/pages/system/synchronizationStatus/syncSettings.vue' /* webpackChunkName: "pages/system/synchronizationStatus/syncSettings" */))
/** 시스템 종료/재시작 ++ */
const systemReStart = () => interopDefault(import('~/pages/system/systemReStart.vue' /* webpackChunkName: "pages/system/systemReStart" */))

/* =============================================
  * Log/Report
  * ============================================= */
/** 현재 상태 - 방화벽 상태 ++ */
/* TODO: 추후 1.2.3 대 NGF 미지원 시 복구 */
// const firewallStatus = () => interopDefault(import('~/pages/logReport/currentStatus/firewallStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/firewallStatus" */))
const firewallCommonStatus = () => interopDefault(import('~/pages/logReport/currentStatus/firewallCommonStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/firewallCommonStatus" */))
/** 현재 상태 - VPN 상태 ++ */
/* TODO: 추후 1.2.3 대 NGF 미지원 시 복구 */
// const vpnStatus = () => interopDefault(import('~/pages/logReport/currentStatus/vpnStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/vpnStatus" */))
const vpnCommonStatus = () => interopDefault(import('~/pages/logReport/currentStatus/vpnCommonStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/vpnCommonStatus" */))
/** 현재 상태 - AutoScaling 상태 ++ */
const autoCommonStatus = () => interopDefault(import('~/pages/logReport/currentStatus/autoCommonStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/autoCommonStatus" */))
/** 현재 상태 - SD-WAN 터널 상태 - 터널 상태 ++ */
const sdwanTunnelStatus = () => interopDefault(import('~/pages/logReport/currentStatus/sdwanTunnelStatus/tunnelStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/sdwanTunnelStatus/tunnelStatus" */))
/** 현재 상태 - SD-WAN 터널 상태 - 토폴로지 상태 ++ */
const sdwanTopologyStatus = () => interopDefault(import('~/pages/logReport/currentStatus/sdwanTunnelStatus/topologyStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/sdwanTunnelStatus/topologyStatus" */))
/** 현재 상태 - WIPS 센서 현재 상태 ++ */
const wipsSensorStatus = () => interopDefault(import('~/pages/logReport/currentStatus/wipsSensorStatus.vue' /* webpackChunkName: "pages/logReport/currentStatus/vpnCommonStatus" */))

const logReport = () => interopDefault(import('~/pages/logReport.vue' /* webpackChunkName: "pages/logReport" */))
/** 관리장비 로그 - 세션 로그 */
const aptSessionLog = () => interopDefault(import('~/pages/logReport/log/sessionLog/aptSessionLog.vue' /* webpackChunkName: "pages/logReport/log/sessionLog/aptSessionLog" */))
const trafficSessionLog = () => interopDefault(import('~/pages/logReport/log/sessionLog/trafficSessionLog.vue' /* webpackChunkName: "pages/logReport/log/sessionLog/trafficSessionLog" */))
const adsSessionLog = () => interopDefault(import('~/pages/logReport/log/sessionLog/ADSSessionLog.vue' /* webpackChunkName: "pages/logReport/log/sessionLog/ADSSessionLog" */))
/** 관리장비 로그 - 보안 로그 */
const threatLog = () => interopDefault(import('~/pages/logReport/log/securityLog/threatLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/threatLog" */))
const dataFilteringLog = () => interopDefault(import('~/pages/logReport/log/securityLog/dataFilteringLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/dataFilteringLog" */))
const webFilterLog = () => interopDefault(import('~/pages/logReport/log/securityLog/webFilterLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/webFilterLog" */))
const antiSpamLog = () => interopDefault(import('~/pages/logReport/log/securityLog/antiSpamLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/antiSpamLog" */))
const appControlLog = () => interopDefault(import('~/pages/logReport/log/securityLog/appControlLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/appControlLog" */))
const aptAttackLog = () => interopDefault(import('~/pages/logReport/log/securityLog/aptAttackLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/aptAttackLog" */))
const authBaseDefenseLog = () => interopDefault(import('~/pages/logReport/log/adsLog/authBaseDefenseLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/authBaseDefenseLog" */))
const blacklistBlockLog = () => interopDefault(import('~/pages/logReport/log/adsLog/blacklistBlockLog.vue' /* webpackChunkName: "pages/logReport/log/securityLog/blacklistBlockLog" */))
/** 관리장비 로그 - 방화벽 로그 */
const fwTrafficLog = () => interopDefault(import('~/pages/logReport/log/firewallLog/fwTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/firewallLog/fwTrafficLog" */))
const fwPolicyTrafficLog = () => interopDefault(import('~/pages/logReport/log/firewallLog/fwPolicyTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/firewallLog/fwPolicyTrafficLog" */))
const natSessionLog = () => interopDefault(import('~/pages/logReport/log/firewallLog/natSessionLog.vue' /* webpackChunkName: "pages/logReport/log/firewallLog/natSessionLog" */))
const adGroupMappingLog = () => interopDefault(import('~/pages/logReport/log/firewallLog/adGroupMappingLog.vue' /* webpackChunkName: "pages/logReport/log/firewallLog/adGroupMappingLog" */))
const userCertLog = () => interopDefault(import('~/pages/logReport/log/firewallLog/userCertLog.vue' /* webpackChunkName: "pages/logReport/log/firewallLog/userCertLog" */))
const domainObjIpLog = () => interopDefault(import('~/pages/logReport/log/firewallLog/domainObjIpLog.vue' /* webpackChunkName: "pages/logReport/log/firewallLog/domainObjIpLog" */))
const deviceEventLog = () => interopDefault(import('~/pages/logReport/log/firewallLog/deviceEventLog.vue' /* webpackChunkName: "pages/logReport/log/firewallLog/deviceEventLog" */))
/** 관리장비 로그 - IPS/DDoS 로그 */
const ipsddosAttackLog = () => interopDefault(import('~/pages/logReport/log/ipsddosLog/ipsddosAttackLog.vue' /* webpackChunkName: "pages/logReport/log/ipsddosLog/ipsddosAttackLog" */))
const ddosProtectDomainLog = () => interopDefault(import('~/pages/logReport/log/ipsddosLog/ddosProtectDomainLog.vue' /* webpackChunkName: "pages/logReport/log/ipsddosLog/ddosProtectDomainLog" */))
const ddosProDomTcpFlagLog = () => interopDefault(import('~/pages/logReport/log/ipsddosLog/ddosProDomTcpFlagLog.vue' /* webpackChunkName: "pages/logReport/log/ipsddosLog/ddosProDomTcpFlagLog" */))
const ipsddosTrafficLog = () => interopDefault(import('~/pages/logReport/log/ipsddosLog/ipsddosTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/ipsddosLog/ipsddosTrafficLog" */))
/** 관리장비 로그 - VPN 로그 */
const ipSecVpnEvtLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/ipSecVpnEvtLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/ipSecVpnEvtLog" */))
const ipSecVpnLineUsageCntLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/ipSecVpnLineUsageCntLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/ipSecVpnLineUsageCntLog" */))
const ipSecVpnTunnelLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/ipSecVpnTunnelLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/ipSecVpnTunnelLog" */))
const pptpL2tpLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/pptpL2tpLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/pptpL2tpLog" */))
const sslVpnSessionLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/sslVpnSessionLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/sslVpnSessionLog" */))
const sslVpnUserCertLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/sslVpnUserCertLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/sslVpnUserCertLog" */))
const sslVpnCertLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/sslVpnCertLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/sslVpnCertLog" */))
const sslVpnClientLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/sslVpnClientLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/sslVpnClientLog" */))
const ipSecVpnTunnelIDLineUsageLog = () => interopDefault(import('~/pages/logReport/log/vpnLog/ipSecVpnTunnelIDLineUsageLog.vue' /* webpackChunkName: "pages/logReport/log/vpnLog/ipSecVpnTunnelLineUsageCntLog" */))
/** 관리장비 로그 - SD-WAN 로그 */
const sdwanTunnelEventLog = () => interopDefault(import('~/pages/logReport/log/sdwanLog/sdwanTunnelEventLog.vue' /* webpackChunkName: "pages/logReport/log/sdwanLog/sdwanTunnelEventLog" */))
const sdwanRuleUseCntLog = () => interopDefault(import('~/pages/logReport/log/sdwanLog/sdwanRuleUseCntLog.vue' /* webpackChunkName: "pages/logReport/log/sdwanLog/sdwanRuleUseCntLog" */))
/** 관리장비 로그 - WIPS 로그*/
const wipsEventLog = () => interopDefault(import('~/pages/logReport/log/wipsLog/wipsEventLog.vue' /* webpackChunkName: "pages/logReport/log/wipsLog/wipsEventLog" */))
const blockLog = () => interopDefault(import('~/pages/logReport/log/wipsLog/blockLog.vue' /* webpackChunkName: "pages/logReport/log/wipsLog/blockLog" */))
const sensorLog = () => interopDefault(import('~/pages/logReport/log/wipsLog/sensorLog.vue' /* webpackChunkName: "pages/logReport/log/wipsLog/sensorLog" */))
/** 관리장비 로그 - 콘텐츠 보안 로그 */
const sslSessionLog = () => interopDefault(import('~/pages/logReport/log/contentSecLog/sslSessionLog.vue' /* webpackChunkName: "pages/logReport/log/contentSecLog/sslSessionLog" */))
const officeKeeperListLog = () => interopDefault(import('~/pages/logReport/log/contentSecLog/officeKeeperListLog.vue' /* webpackChunkName: "pages/logReport/log/contentSecLog/officeKeeperListLog" */))
const officeKeeperBlockLog = () => interopDefault(import('~/pages/logReport/log/contentSecLog/officeKeeperBlockLog.vue' /* webpackChunkName: "pages/logReport/log/contentSecLog/officeKeeperBlockLog" */))
const aptCodeAnalysisLog = () => interopDefault(import('~/pages/logReport/log/contentSecLog/aptCodeAnalysisLog.vue' /* webpackChunkName: "pages/logReport/log/contentSecLog/aptCodeAnalysisLog" */))
const endpointInterworkLog = () => interopDefault(import('~/pages/logReport/log/contentSecLog/endpointInterworkLog.vue' /* webpackChunkName: "pages/logReport/log/contentSecLog/endpointInterworkLog" */))
const resourceRecognitionLog = () => interopDefault(import('~/pages/logReport/log/contentSecLog/resourceRecognitionLog.vue' /* webpackChunkName: "pages/logReport/log/contentSecLog/resourceRecognitionLog" */))
/** 관리장비 로그 - 시스템 로그 ++ */
const haActLog = () => interopDefault(import('~/pages/logReport/log/systemLog/haActLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/haActLog" */))
const haTrafficLog = () => interopDefault(import('~/pages/logReport/log/systemLog/haTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/haTrafficLog" */))
const haStatusLog = () => interopDefault(import('~/pages/logReport/log/systemLog/haStatusLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/haStatusLog" */))
const iapLinkageLog = () => interopDefault(import('~/pages/logReport/log/systemLog/iapLinkageLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/iapLinkageLog" */))
const zombiePcDetectLinkageLog = () => interopDefault(import('~/pages/logReport/log/systemLog/zombiePcDetectLinkageLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/zombiePcDetectLinkageLog" */))
const blackListRegLog = () => interopDefault(import('~/pages/logReport/log/systemLog/blackListRegLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/blackListRegLog" */))
const lineMgmtLog = () => interopDefault(import('~/pages/logReport/log/systemLog/lineMgmtLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/lineMgmtLog" */))
const systemResourceLog = () => interopDefault(import('~/pages/logReport/log/systemLog/systemResourceLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/systemResourceLog" */))
const systemDemonWatch = () => interopDefault(import('~/pages/logReport/log/systemLog/systemDemonWatch.vue' /* webpackChunkName: "pages/logReport/log/systemLog/systemDemonWatch" */))
const systemIfTrafficLog = () => interopDefault(import('~/pages/logReport/log/systemLog/systemIfTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/systemIfTrafficLog" */))
const vlanTrafficLog = () => interopDefault(import('~/pages/logReport/log/systemLog/vlanTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/vlanTrafficLog" */))
const overSubscriptionLog = () => interopDefault(import('~/pages/logReport/log/systemLog/overSubscriptionLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/overSubscriptionLog" */))
const autoScalingEventLog = () => interopDefault(import('~/pages/logReport/log/systemLog/autoScalingEventLog.vue' /* webpackChunkName: "pages/logReport/log/systemLog/autoScalingEventLog" */))
/** 관리장비 로그 - 트래픽 로그 ++ */
const fullTrafficLog = () => interopDefault(import('~/pages/logReport/log/trafficLog/fullTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/trafficLog/fullTrafficLog" */))
const domainTrafficLog = () => interopDefault(import('~/pages/logReport/log/trafficLog/domainTrafficLog.vue' /* webpackChunkName: "pages/logReport/log/trafficLog/domainTrafficLog" */))
/** 관리장비 로그 - 트래픽 제어 로그 ++ */
const qosControlLog = () => interopDefault(import('~/pages/logReport/log/trafficControlLog/qosControlLog.vue' /* webpackChunkName: "pages/logReport/log/trafficControlLog/qosControlLog" */))
const qosDynamicControlLog = () => interopDefault(import('~/pages/logReport/log/trafficControlLog/qosDynamicControlLog.vue' /* webpackChunkName: "pages/logReport/log/trafficControlLog/qosDynamicControlLog" */))
const aclLog = () => interopDefault(import('~/pages/logReport/log/trafficControlLog/aclLog.vue' /* webpackChunkName: "pages/logReport/log/trafficControlLog/aclLog" */))
/** 관리장비 로그 - 경고 로그 */
const warningLog = () => interopDefault(import('~/pages/logReport/log/warningLog.vue' /* webpackChunkName: "pages/logReport/log/warningLog" */))
/** 관리장비 로그 - 관리자 로그 */
const managerLog = () => interopDefault(import('~/pages/logReport/log/managerLog.vue' /* webpackChunkName: "pages/logReport/log/managerLog" */))
/** Prism 로그 - 이벤트 로그 */
const eventLog = () => interopDefault(import('~/pages/logReport/prismLog/eventLog.vue' /* webpackChunkName: "pages/logReport/prismLog/eventLog" */))
/** Prism 로그 - 관리자 로그 */
const adminLog = () => interopDefault(import('~/pages/logReport/prismLog/adminLog.vue' /* webpackChunkName: "pages/logReport/prismLog/adminLog" */))
/** Prism 로그 - 시스템 자원 */
const tamsSystemResourceLog = () => interopDefault(import('~/pages/logReport/prismLog/tamsSystemResourceLog.vue' /* webpackChunkName: "pages/logReport/prismLog/tamsSystemResourceLog" */))
/** Prism 로그 - Ticket 브라우저 */
const ticketBrowser = () => interopDefault(import('~/pages/logReport/prismLog/ticketBrowser.vue' /* webpackChunkName: "pages/logReport/prismLog/ticketBrowser" */))
/** 로그 관리 - 수집/전송 */
const logSetting = () => interopDefault(import('~/pages/logReport/logSettings/logSetting.vue' /* webpackChunkName: "pages/logReport/logSettings/logSetting" */))
/** 로그 관리 - 로그 관리 설정 ++ */
const logControlSetting = () => interopDefault(import('~/pages/logReport/monitoringSettings/logMgmtSettings.vue' /* webpackChunkName: "pages/logReport/monitoringSettings/logMgmtSettings" */))
/** 로그 관리 - 로그 브라우저 ++ */
const logBrowser = () => interopDefault(import('~/pages/logReport/monitoringSettings/logBrowser.vue' /* webpackChunkName: "pages/logReport/monitoringSettings/logBrowser" */))
/** 리포트 - 리포트 설정 */
const reportSettings = () => interopDefault(import('~/pages/logReport/report/reportSettings.vue' /* webpackChunkName: "pages/logReport/report/reportSettings" */))
/** 리포트 - 리포트 브라우저 */
const reportBrowser = () => interopDefault(import('~/pages/logReport/report/reportBrowser.vue' /* webpackChunkName: "pages/logReport/report/reportBrowser" */))
/** 리포트 - 리포트 프로파일 ++ */
const reportProfile = () => interopDefault(import('~/pages/logReport/report/reportProfile.vue' /* webpackChunkName: "pages/logReport/report/reportProfile" */))

/* =============================================
  * CM (Central Management)
  * ============================================= */
const cm = () => interopDefault(import('~/pages/cm.vue' /* webpackChunkName: "pages/cm" */))
/** 보안 정책 - 방화벽 */
const firewallPolicySettings = () => interopDefault(import('~/pages/cm/securityPolicy/firewall/firewallPolicySettings.vue' /* webpackChunkName: "pages/cm/securityPolicy/firewall/firewallPolicySettings" */))
const firewallAdSettings = () => interopDefault(import('~/pages/cm/securityPolicy/firewall/firewallAdSettings.vue' /* webpackChunkName: "pages/cm/securityPolicy/firewall/firewallAdSettings" */))
const ipv6FirewallPolicySettings = () => interopDefault(import('~/pages/cm/securityPolicy/firewall/ipv6FirewallPolicySettings.vue' /* webpackChunkName: "pages/cm/securityPolicy/firewall/ipv6FirewallPolicySettings" */))
const ipv6FirewallAdSettings = () => interopDefault(import('~/pages/cm/securityPolicy/firewall/ipv6FirewallAdSettings.vue' /* webpackChunkName: "pages/cm/securityPolicy/firewall/ipv6FirewallAdSettings" */))
const twoLayerDefense = () => interopDefault(import('~/pages/cm/securityPolicy/firewall/twoLayerDefense.vue' /* webpackChunkName: "pages/cm/securityPolicy/firewall/twoLayerDefense" */))
/** 보안 정책 - 블랙 리스트 */
const blackListReg = () => interopDefault(import('~/pages/cm/securityPolicy/blackList/blackListReg.vue' /* webpackChunkName: "pages/cm/securityPolicy/blackList/blackListReg" */))
const blackListExtLinkage = () => interopDefault(import('~/pages/cm/securityPolicy/blackList/blackListExtLinkage.vue' /* webpackChunkName: "pages/cm/securityPolicy/blackList/blackListExtLinkage" */))
/** 보안 정책 - QoS */
const qosPolicySettings = () => interopDefault(import('~/pages/cm/securityPolicy/qos/qosPolicySettings.vue' /* webpackChunkName: "pages/cm/securityPolicy/qos/qosPolicySettings" */))
const qosProfileSettings = () => interopDefault(import('~/pages/cm/securityPolicy/qos/qosProfileSettings.vue' /* webpackChunkName: "pages/cm/securityPolicy/qos/qosProfileSettings" */))

/** 네트워크 - 보호 도메인 */
const protectionDomain = () => interopDefault(import('~/pages/cm/securityPolicy/network/protectionDomain.vue' /* webpackChunkName: "pages/cm/securityPolicy/network/protectionDomain" */))
const adsPolicyDomain = () => interopDefault(import('~/pages/cm/securityPolicy/network/AdsPolicyDomain.vue' /* webpackChunkName: "pages/cm/securityPolicy/network/AdsPolicyDomain" */))

/** 위협 정책 - 보안 센터 정책 */
const securityCenterPolicy = () => interopDefault(import('~/pages/cm/securityPolicy/threatPolicy/securityCenterPolicy.vue' /* webpackChunkName: "pages/cm/securityPolicy/threatPolicy/securityCenterPolicy" */))
const popSnortSignature = () => interopDefault(import('~/pages/cm/securityPolicy/threatPolicy/popSnortSignature.vue' /* webpackChunkName: "pages/cm/securityPolicy/threatPolicy/popSnortSignature" */))
const popYaraSignature = () => interopDefault(import('~/pages/cm/securityPolicy/threatPolicy/popYaraSignature.vue' /* webpackChunkName: "pages/cm/securityPolicy/threatPolicy/popYaraSignature" */))

/** 객체 - 객체 */
const objectSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/object/objectSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/object/objectSettings" */))
const ipsObjectSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/object/ipsObjectSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/object/ipsObjectSettings" */))
const objectMgmtSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/object/objectMgmtSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/object/objectMgmtSettings" */))
/** 객체 - IPS */
const ipsProfileSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/ips/ipsProfileSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/ips/ipsProfileSettings" */))
const ipsGeneralSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/ips/ipsGeneralSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/ips/ipsGeneralSettings" */))
const ipsIDProfileSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/ips/ipsIDProfileSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/ips/ipsIDProfileSettings" */))
const ipsProtectionProfileSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/ips/ipsProtectionProfileSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/ips/ipsProtectionProfileSettings" */))
const ipsIDCommonPolicy = () => interopDefault(import('~/pages/cm/objectSecProfile/ips/ipsIDCommonPolicy.vue' /* webpackChunkName: "pages/cm/objectSecProfile/ips/ipsIDCommonPolicy" */))
/** 객체 - 안티 바이러스 */
const antiVirusProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/antiVirus/antiVirusProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiVirus/antiVirusProfile" */))
const checkExpList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiVirus/checkExpList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiVirus/checkExpList" */))
/** 객체 - 안티 스팸 */
const antiSpamProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/antiSpamProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/antiSpamProfile" */))
const blockMailAddrList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/blockMailAddrList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/blockMailAddrList" */))
const exceptionList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/exceptionList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/exceptionList" */))
const forbiddenWordList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/forbiddenWordList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/forbiddenWordList" */))
const protectMailServerList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/protectMailServerList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/protectMailServerList" */))
const rblServerList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/rblServerList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/rblServerList" */))
const smtpForbiddenCmdList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/smtpForbiddenCmdList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/smtpForbiddenCmdList" */))
const spamChkServerList = () => interopDefault(import('~/pages/cm/objectSecProfile/antiSpam/spamChkServerList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/antiSpam/spamChkServerList" */))
/** 객체 - 웹 필터 */
const webFilterProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/webFilter/webFilterProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/webFilter/webFilterProfile" */))
const anonymizerServerList = () => interopDefault(import('~/pages/cm/objectSecProfile/webFilter/anonymizerServerList.vue' /* webpackChunkName: "pages/cm/objectSecProfile/webFilter/anonymizerServerList" */))
/** 객체 - DLP */
const dlpProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/dlp/dlpProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/dlp/dlpProfile" */))
const dlpDataObject = () => interopDefault(import('~/pages/cm/objectSecProfile/dlp/dlpDataObject.vue' /* webpackChunkName: "pages/cm/objectSecProfile/dlp/dlpDataObject" */))
const dlpDataGroupObject = () => interopDefault(import('~/pages/cm/objectSecProfile/dlp/dlpDataGroupObject.vue' /* webpackChunkName: "pages/cm/objectSecProfile/dlp/dlpDataGroupObject" */))
const dlpExpProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/dlp/dlpExpProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/dlp/dlpExpProfile" */))
/** 객체 - 파일 유형 제어 */
const fileTypeCtrlProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/fileTypeControl/fileTypeCtrlProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/fileTypeControl/fileTypeCtrlProfile" */))
const fileTypeCtrlExpProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/fileTypeControl/fileTypeCtrlExpProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/fileTypeControl/fileTypeCtrlExpProfile" */))
/** 객체 - APT */
const aptCmnSettings = () => interopDefault(import('~/pages/cm/objectSecProfile/apt/aptCmnSettings.vue' /* webpackChunkName: "pages/cm/objectSecProfile/apt/aptCmnSettings" */))
const aptProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/apt/aptProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/apt/aptProfile" */))
const aptExceptProfile = () => interopDefault(import('~/pages/cm/objectSecProfile/apt/aptExceptProfile.vue' /* webpackChunkName: "pages/cm/objectSecProfile/apt/aptExceptProfile" */))
const aptExceptObject = () => interopDefault(import('~/pages/cm/objectSecProfile/apt/aptExceptObject.vue' /* webpackChunkName: "pages/cm/objectSecProfile/apt/aptExceptObject" */))
/** VPN - IPSec VPN */
const branchConnSettings = () => interopDefault(import('~/pages/cm/vpn/ipSecVpn/branchConnSettings.vue' /* webpackChunkName: "pages/cm/vpn/ipSecVpn/branchConnSettings" */))
const pointReg = () => interopDefault(import('~/pages/cm/vpn/ipSecVpn/pointReg.vue' /* webpackChunkName: "pages/cm/vpn/ipSecVpn/pointReg" */))
const secPolicySettings = () => interopDefault(import('~/pages/cm/vpn/ipSecVpn/secPolicySettings.vue' /* webpackChunkName: "pages/cm/vpn/ipSecVpn/secPolicySettings" */))
const centerConnSettings = () => interopDefault(import('~/pages/cm/vpn/ipSecVpn/centerConnSettings.vue' /* webpackChunkName: "pages/cm/vpn/ipSecVpn/centerConnSettings" */))
/** Device System - 인증 서버 */
const authenticationServer = () => interopDefault(import('~/pages/cm/deviceSystem/authentication/authenticationServer.vue' /* webpackChunkName: "pages/cm/deviceSystem/authentication/authenticationServer" */))
/** Device System - 연동 서버 */
const mailServerSettings = () => interopDefault(import('~/pages/cm/deviceSystem/linkageServer/mailServerSettings.vue' /* webpackChunkName: "pages/cm/deviceSystem/linkageServer/mailServerSettings" */))
const syslogSettings = () => interopDefault(import('~/pages/cm/deviceSystem/linkageServer/syslogSettings.vue' /* webpackChunkName: "pages/cm/deviceSystem/linkageServer/syslogSettings" */))
const snmpSettings = () => interopDefault(import('~/pages/cm/deviceSystem/linkageServer/snmpSettings.vue' /* webpackChunkName: "pages/cm/deviceSystem/linkageServer/snmpSettings" */))
/** Device System - 경고 페이지 설정 ++ */
const warningPageSettings = () => interopDefault(import('~/pages/cm/deviceSystem/warningPageSettingsGroup/warningPageSettings.vue' /* webpackChunkName: "pages/cm/deviceSystem/warningPageSettingsGroup/warningPageSettings" */))
/** Device System - 관리자 설정 ++ */
const cmAdministrator = () => interopDefault(import('~/pages/cm/deviceSystem/adminSettings/administrator.vue' /* webpackChunkName: "pages/cm/deviceSystem/adminSettings/administrator" */))
const cmAdminIpSettings = () => interopDefault(import('~/pages/cm/deviceSystem/adminSettings/adminIpSettings.vue' /* webpackChunkName: "pages/cm/deviceSystem/adminSettings/adminIpSettings" */))
const cmAdminGeneralSettings = () => interopDefault(import('~/pages/cm/deviceSystem/adminSettings/adminGeneralSettings.vue' /* webpackChunkName: "pages/cm/deviceSystem/adminSettings/adminGeneralSettings" */))
/** Device System - 설정 백업 스케쥴  */
const settingsBackupSchedule = () => interopDefault(import('~/pages/cm/deviceSystem/backupRestore/settingsBackupSchedule.vue' /* webpackChunkName: "pages/cm/deviceSystem/backupRestore/settingsBackupSchedule" */))
/** Device System - 백업/복구 */
const backupRestore = () => interopDefault(import('~/pages/cm/deviceSystem/backupRestore/backupRestore.vue' /* webpackChunkName: "pages/cm/deviceSystem/backupRestore/backupRestore" */))
/** 통합 관리 - 통합 스크립트 ++ */
const integrateScript = () => interopDefault(import('~/pages/cm/integrateMgmt/integrateScript.vue' /* webpackChunkName: "pages/cm/integrateMgmt/integrateScript" */))
/** 로그/리포트 - 리포트 설정 ++ */
const cmReportSettings = () => interopDefault(import('~/pages/cm/logReport/reportSettings/reportSettings.vue' /* webpackChunkName: "pages/cm/logReport/reportSettings/reportSettings" */))
/** 로그/리포트 - 모니터링 설정 */
const logMgmtSettings = () => interopDefault(import('~/pages/cm/logReport/monitoringSettings/logMgmtSettings.vue' /* webpackChunkName: "pages/cm/logReport/monitoringSettings/logMgmtSettings" */))

/** SD-WAN - SD-WAN 기본 설정 - 경로 레이블 */
const sdwanPathProfile = () => interopDefault(import('~/pages/cm/sdwan/sdwanBasicSetting/sdwanPathProfile.vue' /* webpackChunkName: "pages/cm/sdwan/sdwanBasicSetting/sdwanPathProfile" */))
/** SD-WAN - SD-WAN 기본 설정 - SD-WAN 기본 설정 프로파일 */
const sdwanDefaultPolicy = () => interopDefault(import('~/pages/cm/sdwan/sdwanBasicSetting/sdwanDefaultPolicy.vue' /* webpackChunkName: "pages/cm/sdwan/sdwanBasicSetting/sdwanDefaultPolicy" */))
/** SD-WAN - 장비 설정 - 인터페이스 설정 */
const sdwanInterfaceSetting = () => interopDefault(import('~/pages/cm/sdwan/deviceSetting/sdwanInterfaceSetting.vue' /* webpackChunkName: "pages/cm/sdwan/deviceSetting/sdwanInterfaceSetting" */))
/** SD-WAN - 장비 설정 - 라우팅 설정 */
const sdwanRoutingSetting = () => interopDefault(import('~/pages/cm/sdwan/deviceSetting/sdwanRoutingSetting.vue' /* webpackChunkName: "pages/cm/sdwan/deviceSetting/sdwanRoutingSetting" */))
/** SD-WAN - SD-WAN 설정 - 정책 설정 */
const sdwanPolicySetting = () => interopDefault(import('~/pages/cm/sdwan/sdwanSetting/sdwanPolicySetting.vue' /* webpackChunkName: "pages/cm/sdwan/sdwanSetting/sdwanPolicySetting" */))
/** SD-WAN - SD-WAN 설정 - 프로파일 설정 */
const sdwanProfileSetting = () => interopDefault(import('~/pages/cm/sdwan/sdwanSetting/sdwanProfileSetting.vue' /* webpackChunkName: "pages/cm/sdwan/sdwanSetting/sdwanProfileSetting" */))
/** SD-WAN - 터널 설정 - 터널 그룹 설정 */
const sdwanTunnelGroupSetting = () => interopDefault(import('~/pages/cm/sdwan/sdwanTunnelSetting.vue' /* webpackChunkName: "pages/cm/sdwan/sdwanTunnelSetting" */))

/* =============================================
  * TA (Threat Analysis)
  * ============================================= */
const analysis = () => interopDefault(import('~/pages/analysis.vue' /* webpackChunkName: "pages/analysis" */))
/** 트래픽 ++ */
const trafficAnalysis = () => interopDefault(import('~/pages/analysis/traffic/trafficAnalysis.vue' /* webpackChunkName: "pages/analysis/traffic/trafficAnalysis" */))
const sslTrafficAnalysis = () => interopDefault(import('~/pages/analysis/traffic/sslTrafficAnalysis.vue' /* webpackChunkName: "pages/analysis/traffic/sslTrafficAnalysis" */))
const trafficStatistics = () => interopDefault(import('~/pages/analysis/traffic/trafficStatistics.vue' /* webpackChunkName: "pages/analysis/traffic/trafficStatistics" */))
const domainTrafficStatistics = () => interopDefault(import('~/pages/analysis/traffic/domainTrafficStatistics.vue' /* webpackChunkName: "pages/analysis/traffic/domainTrafficStatistics" */))
/** 트래픽 제어 통계 ++ */
const analysisAcl4 = () => interopDefault(import('~/pages/analysis/trafficControl/analysisAcl4.vue' /* webpackChunkName: "pages/analysis/trafficControl/analysisAcl4" */))
const analysisAcl6 = () => interopDefault(import('~/pages/analysis/trafficControl/analysisAcl6.vue' /* webpackChunkName: "pages/analysis/trafficControl/analysisAcl6" */))
const analysisMac = () => interopDefault(import('~/pages/analysis/trafficControl/analysisMac.vue' /* webpackChunkName: "pages/analysis/trafficControl/analysisMac" */))
const qosStatistics = () => interopDefault(import('~/pages/analysis/trafficControl/qosStatistics.vue' /* webpackChunkName: "pages/analysis/trafficControl/qosStatistics" */))
const dynamicQosStatistics = () => interopDefault(import('~/pages/analysis/trafficControl/dynamicQosStatistics.vue' /* webpackChunkName: "pages/analysis/trafficControl/dynamicQosStatistics" */))
/** 보안 ++ */
const securityAnalysis = () => interopDefault(import('~/pages/analysis/security/securityAnalysis.vue' /* webpackChunkName: "pages/analysis/security/securityAnalysis" */))
/** 시스템 ++ */
const interfaceAnalysis = () => interopDefault(import('~/pages/analysis/system/interfaceAnalysis.vue' /* webpackChunkName: "pages/analysis/system/interfaceAnalysis" */))
const vlanTrafficAnalysis = () => interopDefault(import('~/pages/analysis/system/vlanTrafficAnalysis.vue' /* webpackChunkName: "pages/analysis/system/vlanTrafficAnalysis" */))
const qosAnalysis = () => interopDefault(import('~/pages/analysis/system/qosAnalysis.vue' /* webpackChunkName: "pages/analysis/system/qosAnalysis" */))
/** VPN */
const ipsecVpnAnalysis = () => interopDefault(import('~/pages/analysis/vpn/ipsecVpnAnalysis.vue' /* webpackChunkName: "pages/analysis/vpn/ipsecVpnAnalysis" */))

/* =============================================
  * PAMS
  * ============================================= */
const policyAnalysis = () => interopDefault(import('~/pages/policyAnalysis.vue' /* webpackChunkName: "pages/policyAnalysis" */))
/** 설정 ++ */
const useFirewall = () => interopDefault(import('~/pages/policyManagement/settings/useFirewall.vue' /* webpackChunkName: "pages/policyManagement/settings/useFirewall" */))
const internalNetwork = () => interopDefault(import('~/pages/policyManagement/settings/internalNetwork.vue' /* webpackChunkName: "pages/policyManagement/settings/internalNetwork" */))
const forbidNetwork = () => interopDefault(import('~/pages/policyManagement/settings/forbidNetwork.vue' /* webpackChunkName: "pages/policyManagement/settings/forbidNetwork" */))
const overAllowPolicy = () => interopDefault(import('~/pages/policyManagement/settings/overAllowPolicy.vue' /* webpackChunkName: "pages/policyManagement/settings/overAllowPolicy" */))
/** 분석 ++ */
const ruleObjectOptimize = () => interopDefault(import('~/pages/policyManagement/analysis/ruleObjectOptimize.vue' /* webpackChunkName: "pages/policyManagement/analysis/ruleObjectOptimize" */))
const ruleComplianceDetect = () => interopDefault(import('~/pages/policyManagement/analysis/ruleComplianceDetect.vue' /* webpackChunkName: "pages/policyManagement/analysis/ruleComplianceDetect" */))
const ruleObjectTrafficAnalysis = () => interopDefault(import('~/pages/policyManagement/analysis/ruleObjectTrafficAnalysis.vue' /* webpackChunkName: "pages/policyManagement/analysis/ruleObjectTrafficAnalysis" */))
/** PAMS - 설정 ++ */
const generalSettings = () => interopDefault(import('~/pages/policyManagement/pams/settings/generalSettings.vue' /* webpackChunkName: "pages/policyManagement/pams/settings/generalSettings" */))
const firewallMatchRule = () => interopDefault(import('~/pages/policyManagement/pams/settings/firewallMatchRule.vue' /* webpackChunkName: "pages/policyManagement/pams/settings/firewallMatchRule" */))
const firewallZoneSettings = () => interopDefault(import('~/pages/policyManagement/pams/settings/firewallZoneSettings.vue' /* webpackChunkName: "pages/policyManagement/pams/settings/firewallZoneSettings" */))
const requestRuleProcess = () => interopDefault(import('~/pages/policyManagement/pams/ruleAutoMgmt/requestRuleProcess.vue' /* webpackChunkName: "pages/policyManagement/pams/ruleAutoMgmt/requestRuleProcess" */))
/** PAMS - 결과 조회 ++ */
const resultInquiry = () => interopDefault(import('~/pages/policyManagement/pams/resultInquiry/resultInquiry.vue' /* webpackChunkName: "pages/policyManagement/pams/resultInquiry/resultInquiry" */))

const emptyFn = () => {}

Vue.use(Router)

if (process.client) {
  window.history.scrollRestoration = 'manual'
}
export const routerOptions = {
  mode: 'history',
  base: '/',
  linkActiveClass: 'nuxt-link-active',
  linkExactActiveClass: 'nuxt-link-exact-active',
  scrollBehavior,

  routes: [
    /** 공통 */
    {
      path: '/',
      name: 'TAMS',
      component: index
      // redirect: {
      //   name: 'dashboard-dashboard-dashboard'
      // },
    },
    {
      path: '/loginDev',
      name: 'loginDev',
      component: loginDev
    },

    /** Dashboard */
    {
      path: '/dashboard',
      name: 'dashboard',
      component: dashboardParent,
      children: [
        /** 대시보드 */
        {
          path: 'dashboard/dashboard',
          name: 'dashboard-dashboard-dashboard',
          component: dashboard
        },
        /** 국가별 정보 */
        {
          path: 'dashboard/countryInfo',
          name: 'dashboard-dashboard-countryInfo',
          component: countryInfo
        },
        /** 장비 현황 */
        {
          path: 'dashboard/deviceStatus',
          name: 'dashboard-dashboard-deviceStatus',
          component: deviceStatus
        },
        /** 장비 토폴로지 */
        {
          path: 'dashboard/topology',
          name: 'dashboard-dashboard-topology',
          component: topology
        }
      ]
    },

    /** TA (Threat Analysis) */
    {
      path: '/analysis',
      name: 'analysis',
      component: analysis,
      children: [
        /** 트래픽 */
        {
          path: 'traffic/trafficAnalysis',
          name: 'analysis-traffic-trafficAnalysis',
          component: trafficAnalysis
        },
        {
          path: 'traffic/sslTrafficAnalysis',
          name: 'analysis-traffic-sslTrafficAnalysis',
          component: sslTrafficAnalysis
        },
        {
          path: 'traffic/trafficStatistics',
          name: 'analysis-traffic-trafficStatistics',
          component: trafficStatistics
        },
        {
          path: 'traffic/domainTrafficStatistics',
          name: 'analysis-traffic-domainTrafficStatistics',
          component: domainTrafficStatistics
        },
        {
          path: 'trafficControl/analysisAcl4',
          name: 'analysis-trafficControl-analysisAcl4',
          component: analysisAcl4
        },
        {
          path: 'trafficControl/analysisAcl6',
          name: 'analysis-trafficControl-analysisAcl6',
          component: analysisAcl6
        },
        {
          path: 'trafficControl/analysisMac',
          name: 'analysis-trafficControl-analysisMac',
          component: analysisMac
        },
        {
          path: 'trafficControl/qosStatistics',
          name: 'analysis-trafficControl-qosStatistics',
          component: qosStatistics
        },
        {
          path: 'trafficControl/dynamicQosStatistics',
          name: 'analysis-trafficControl-dynamicQosStatistics',
          component: dynamicQosStatistics
        },
        /** 보안 */
        {
          path: 'security/securityAnalysis',
          name: 'analysis-security-securityAnalysis',
          component: securityAnalysis
        },
        /** VPN */
        {
          path: 'vpn/ipsecVpnAnalysis',
          name: 'analysis-vpn-ipsecVpnAnalysis',
          component: ipsecVpnAnalysis
        },
        /** 시스템 */
        {
          path: 'system/interfaceAnalysis',
          name: 'analysis-system-interfaceAnalysis',
          component: interfaceAnalysis
        },
        {
          path: 'system/vlanTrafficAnalysis',
          name: 'analysis-system-vlanTrafficAnalysis',
          component: vlanTrafficAnalysis
        },
        {
          path: 'system/qosAnalysis',
          name: 'analysis-system-qosAnalysis',
          component: qosAnalysis
        }
      ]
    },

    /** Log / Report */
    {
      path: '/logReport',
      name: 'logReport',
      component: logReport,
      children: [
        /** 현재 상태 - 방화벽 상태 ++ */
        {
          path: 'CurrentStatus/FWStatus',
          name: 'logReport-CurrentStatus-FWStatus',
          component: firewallCommonStatus
        },
        /** 현재 상태 - VPN 상태 ++ */
        {
          path: 'CurrentStatus/VPNStatus',
          name: 'logReport-CurrentStatus-VPNStatus',
          component: vpnCommonStatus
        },
        /** 현재 상태 - VPN 상태 ++ */
        {
          path: 'CurrentStatus/autoScalingStatus',
          name: 'logReport-CurrentStatus-autoScalingStatus',
          component: autoCommonStatus
        },
        /** 현재 상태 - SD-WAN 상태 - 터널 상태 ++ */
        {
          path: 'CurrentStatus/sdwanTunnelStatus/tunnelStatus',
          name: 'logReport-CurrentStatus-sdwanTunnelStatus-tunnelStatus',
          component: sdwanTunnelStatus
        },
        /** 현재 상태 - SD-WAN 상태 - 토폴로지 상태 ++ */
        {
          path: 'CurrentStatus/sdwanTunnelStatus/topologyStatus',
          name: 'logReport-CurrentStatus-sdwanTunnelStatus-topologyStatus',
          component: sdwanTopologyStatus
        },
        /** 현재 상태 - WIPS 센서 현재 상태 ++ */
        {
          path: 'CurrentStatus/wipsSensorStatus',
          name: 'logReport-CurrentStatus-wipsSensorStatus',
          component: wipsSensorStatus
        },
        /** 관리장비 로그 - 세션 로그 */
        {
          path: 'log/sessionLog/aptSessionLog',
          name: 'logReport-log-sessionLog-aptSessionLog',
          component: aptSessionLog
        },
        {
          path: 'log/sessionLog/trafficSessionLog',
          name: 'logReport-log-sessionLog-trafficSessionLog',
          component: trafficSessionLog
        },
        {
          path: 'log/sessionLog/adsSessionLog',
          name: 'logReport-log-sessionLog-adsSessionLog',
          component: adsSessionLog
        },
        /** 관리장비 로그 - 보안 로그 */
        {
          path: 'log/securityLog/threatLog',
          name: 'logReport-log-securityLog-threatLog',
          component: threatLog
        },
        {
          path: 'log/securityLog/webFilterLog',
          name: 'logReport-log-securityLog-webFilterLog',
          component: webFilterLog
        },
        {
          path: 'log/securityLog/dataFilteringLog',
          name: 'logReport-log-securityLog-dataFilteringLog',
          component: dataFilteringLog
        },
        {
          path: 'log/securityLog/antiSpamLog',
          name: 'logReport-log-securityLog-antiSpamLog',
          component: antiSpamLog
        },
        {
          path: 'log/securityLog/appControlLog',
          name: 'logReport-log-securityLog-appControlLog',
          component: appControlLog
        },
        {
          path: 'log/securityLog/aptAttackLog',
          name: 'logReport-log-securityLog-aptAttackLog',
          component: aptAttackLog
        },
        {
          path: 'log/adsLog/authBaseDefenseLog',
          name: 'logReport-log-adsLog-authBaseDefenseLog',
          component: authBaseDefenseLog
        },
        {
          path: 'log/adsLog/blacklistBlockLog',
          name: 'logReport-log-adsLog-blacklistBlockLog',
          component: blacklistBlockLog
        },
        /** 관리장비 로그 - 방화벽 로그 */
        {
          path: 'log/firewallLog/fwTrafficLog',
          name: 'logReport-log-firewallLog-fwTrafficLog',
          component: fwTrafficLog
        },
        {
          path: 'log/firewallLog/fwPolicyTrafficLog',
          name: 'logReport-log-firewallLog-fwPolicyTrafficLog',
          component: fwPolicyTrafficLog
        },
        {
          path: 'log/firewallLog/natSessionLog',
          name: 'logReport-log-firewallLog-natSessionLog',
          component: natSessionLog
        },
        {
          path: 'log/firewallLog/adGroupMappingLog',
          name: 'logReport-log-firewallLog-adGroupMappingLog',
          component: adGroupMappingLog
        },
        {
          path: 'log/firewallLog/userCertLog',
          name: 'logReport-log-firewallLog-userCertLog',
          component: userCertLog
        },
        {
          path: 'log/firewallLog/domainObjIpLog',
          name: 'logReport-log-firewallLog-domainObjIpLog',
          component: domainObjIpLog
        },
        {
          path: 'log/firewallLog/deviceEventLog',
          name: 'logReport-log-firewallLog-deviceEventLog',
          component: deviceEventLog
        },
        /** 관리장비 로그 - IPS/DDoS 로그 */
        {
          path: 'log/ipsddosLog/ipsddosAttackLog',
          name: 'logReport-log-ipsddosLog-ipsddosAttackLog',
          component: ipsddosAttackLog
        },
        {
          path: 'log/ipsddosLog/ddosProtectDomainLog',
          name: 'logReport-log-ipsddosLog-ddosProtectDomainLog',
          component: ddosProtectDomainLog
        },
        {
          path: 'log/ipsddosLog/ddosProDomTcpFlagLog',
          name: 'logReport-log-ipsddosLog-ddosProDomTcpFlagLog',
          component: ddosProDomTcpFlagLog
        },
        {
          path: 'log/ipsddosLog/ipsddosTrafficLog',
          name: 'logReport-log-ipsddosLog-ipsddosTrafficLog',
          component: ipsddosTrafficLog
        },
        /** 관리장비 로그 - VPN 로그 */
        {
          path: 'log/vpnLog/ipSecVpnEvtLog',
          name: 'logReport-log-vpnLog-ipSecVpnEvtLog',
          component: ipSecVpnEvtLog
        },
        {
          path: 'log/vpnLog/ipSecVpnLineUsageCntLog',
          name: 'logReport-log-vpnLog-ipSecVpnLineUsageCntLog',
          component: ipSecVpnLineUsageCntLog
        },
        {
          path: 'log/vpnLog/ipSecVpnTunnelLog',
          name: 'logReport-log-vpnLog-ipSecVpnTunnelLog',
          component: ipSecVpnTunnelLog
        },
        {
          path: 'log/vpnLog/pptpL2tpLog',
          name: 'logReport-log-vpnLog-pptpL2tpLog',
          component: pptpL2tpLog
        },
        {
          path: 'log/vpnLog/sslVpnSessionLog',
          name: 'logReport-log-vpnLog-sslVpnSessionLog',
          component: sslVpnSessionLog
        },
        {
          path: 'log/vpnLog/sslVpnUserCertLog',
          name: 'logReport-log-vpnLog-sslVpnUserCertLog',
          component: sslVpnUserCertLog
        },
        {
          path: 'log/vpnLog/sslVpnCertLog',
          name: 'logReport-log-vpnLog-sslVpnCertLog',
          component: sslVpnCertLog
        },
        {
          path: 'log/vpnLog/sslVpnClientLog',
          name: 'logReport-log-vpnLog-sslVpnClientLog',
          component: sslVpnClientLog
        },
        {
          path: 'log/vpnLog/ipSecVpnTunnelLineLog',
          name: 'logReport-log-vpnLog-ipSecVpnTunnelLineLog',
          component: ipSecVpnTunnelIDLineUsageLog
        },
        /** 관리장비 로그 - SD-WAN 로그 */
        {
          path: 'log/sd-wanLog/sdwanTunnelEventLog',
          name: 'logReport-log-sd-wanLog-sdwanTunnelEventLog',
          component: sdwanTunnelEventLog
        },
        {
          path: 'log/sd-wanLog/sdwanRuleUseCntLog',
          name: 'logReport-log-sd-wanLog-sdwanRuleUseCntLog',
          component: sdwanRuleUseCntLog
        },
        /** 관리장비 로그 - WIPS 로그 */
        {
          path: 'log/wipsLog/wipsEventLog',
          name: 'logReport-log-wipsLog-wipsEventLog',
          component: wipsEventLog
        },
        {
          path: 'log/wipsLog/blockLog',
          name: 'logReport-log-wipsLog-blockLog',
          component: blockLog
        },
        {
          path: 'log/wipsLog/sensorLog',
          name: 'logReport-log-wipsLog-sensorLog',
          component: sensorLog
        },
        /** 관리장비 로그 - 콘텐츠 보안 로그 */
        {
          path: 'log/contentSecLog/sslSessionLog',
          name: 'logReport-log-contentSecLog-sslSessionLog',
          component: sslSessionLog
        },
        {
          path: 'log/contentSecLog/officeKeeperListLog',
          name: 'logReport-log-contentSecLog-officeKeeperListLog',
          component: officeKeeperListLog
        },
        {
          path: 'log/contentSecLog/officeKeeperBlockLog',
          name: 'logReport-log-contentSecLog-officeKeeperBlockLog',
          component: officeKeeperBlockLog
        },
        {
          path: 'log/contentSecLog/aptCodeAnalysisLog',
          name: 'logReport-log-contentSecLog-aptCodeAnalysisLog',
          component: aptCodeAnalysisLog
        },
        {
          path: 'log/contentSecLog/endpointInterworkLog',
          name: 'logReport-log-contentSecLog-endpointInterworkLog',
          component: endpointInterworkLog
        },
        {
          path: 'log/contentSecLog/resourceRecognitionLog',
          name: 'logReport-log-contentSecLog-resourceRecognitionLog',
          component: resourceRecognitionLog
        },
        /** 관리장비 로그 - 시스템 로그 */
        {
          path: 'log/systemLog/haActLog',
          name: 'logReport-log-systemLog-haActLog',
          component: haActLog
        },
        {
          path: 'log/systemLog/haTrafficLog',
          name: 'logReport-log-systemLog-haTrafficLog',
          component: haTrafficLog
        },
        {
          path: 'log/systemLog/haStatusLog',
          name: 'logReport-log-systemLog-haStatusLog',
          component: haStatusLog
        },
        {
          path: 'log/systemLog/iapLinkageLog',
          name: 'logReport-log-systemLog-iapLinkageLog',
          component: iapLinkageLog
        },
        {
          path: 'log/systemLog/zombiePcDetectLinkageLog',
          name: 'logReport-log-systemLog-zombiePcDetectLinkageLog',
          component: zombiePcDetectLinkageLog
        },
        {
          path: 'log/systemLog/blackListRegLog',
          name: 'logReport-log-systemLog-blackListRegLog',
          component: blackListRegLog
        },
        {
          path: 'log/systemLog/lineMgmtLog',
          name: 'logReport-log-systemLog-lineMgmtLog',
          component: lineMgmtLog
        },
        {
          path: 'log/systemLog/systemResourceLog',
          name: 'logReport-log-systemLog-systemResourceLog',
          component: systemResourceLog
        },
        {
          path: 'log/systemLog/systemDemonWatch',
          name: 'logReport-log-systemLog-systemDemonWatch',
          component: systemDemonWatch
        },
        {
          path: 'log/systemLog/systemIfTrafficLog',
          name: 'logReport-log-systemLog-systemIfTrafficLog',
          component: systemIfTrafficLog
        },
        {
          path: 'log/systemLog/vlanTrafficLog',
          name: 'logReport-log-systemLog-vlanTrafficLog',
          component: vlanTrafficLog
        },
        {
          path: 'log/systemLog/overSubscriptionLog',
          name: 'logReport-log-systemLog-overSubscriptionLog',
          component: overSubscriptionLog
        },
        {
          path: 'log/systemLog/autoscalingEventLog',
          name: 'logReport-log-systemLog-autoscalingEventLog',
          component: autoScalingEventLog
        },
        /** 관리장비 로그 - 트래픽 로그 */
        {
          path: 'log/trafficLog/fullTrafficLog',
          name: 'logReport-log-trafficLog-fullTrafficLog',
          component: fullTrafficLog
        },
        {
          path: 'log/trafficLog/domainTrafficLog',
          name: 'logReport-log-trafficLog-domainTrafficLog',
          component: domainTrafficLog
        },
        /** 관리장비 로그 - 트래픽 제어 로그 */
        {
          path: 'log/trafficControlLog/qosControlLog',
          name: 'logReport-log-trafficControlLog-qosControlLog',
          component: qosControlLog
        },
        {
          path: 'log/trafficControlLog/qosDynamicControlLog',
          name: 'logReport-log-trafficControlLog-qosDynamicControlLog',
          component: qosDynamicControlLog
        },
        {
          path: 'log/trafficControlLog/aclLog',
          name: 'logReport-log-trafficControlLog-aclLog',
          component: aclLog
        },
        /** 관리장비 로그 - 경고 로그 */
        {
          path: 'log/warningLog',
          name: 'logReport-log-warningLog',
          component: warningLog
        },
        /** 관리장비 로그 - 관리자 로그 */
        {
          path: 'log/managerLog',
          name: 'logReport-log-managerLog',
          component: managerLog
        },
        /** TAMS 로그 - 이벤트 로그 */
        {
          path: 'prismLog/eventLog',
          name: 'logReport-prismLog-eventLog',
          component: eventLog
        },
        /** TAMS 로그 - 관리자 로그 */
        {
          path: 'prismLog/adminLog',
          name: 'logReport-prismLog-adminLog',
          component: adminLog
        },
        /** TAMS 로그 - 시스템 자원 */
        {
          path: 'prismLog/tamsSystemResourceLog',
          name: 'logReport-prismLog-tamsSystemResourceLog',
          component: tamsSystemResourceLog
        },
        /** TAMS 로그 - Ticket 브라우저 - by wj_tams_dev01 */
        {
          path: 'prismLog/ticketBrowser',
          name: 'logReport-prismLog-ticketBrowser',
          component: ticketBrowser
        },
        /** 로그 관리 - 로그 수집/전송 */
        {
          path: 'logSettings/logSetting',
          name: 'logReport-logSettings-logSetting',
          component: logSetting
        },
        /** 로그 관리 - 로그 관리 설정 */
        {
          path: 'logSettings/logControlSetting',
          name: 'logReport-logSettings-logControlSetting',
          component: logControlSetting
        },
        /** 로그 관리 - 로그 브라우저 */
        {
          path: 'logSettings/logBrowser',
          name: 'logReport-logSettings-logBrowser',
          component: logBrowser
        },
        /** 리포트 - 리포트 조회 */
        {
          path: 'report/reportBrowser',
          name: 'logReport-report-reportBrowser',
          component: reportBrowser
        },
        /** 리포트 - 리포트 설정 */
        {
          path: 'report/reportSettings',
          name: 'logReport-report-reportSettings',
          component: reportSettings
        },
        /** 리포트 - 리포트 그룹관리 */
        {
          path: 'report/reportGroup',
          name: 'logReport-report-reportGroup',
          component: reportProfile
        }
      ]
    },

    // CM (Central Management)
    {
      path: '/policyManagement',
      name: 'policyManagement',
      component: cm,
      children: [
        /** 보안 정책 - 방화벽 */
        {
          path: 'securityPolicy/firewall/firewallPolicySettings',
          name: 'policyManagement-securityPolicy-firewall-firewallPolicySettings',
          component: firewallPolicySettings
        },
        {
          path: 'securityPolicy/firewall/firewallAdSettings',
          name: 'policyManagement-securityPolicy-firewall-firewallAdSettings',
          component: firewallAdSettings
        },
        {
          path: 'securityPolicy/firewall/ipv6FirewallPolicySettings',
          name: 'policyManagement-securityPolicy-firewall-ipv6FirewallPolicySettings',
          component: ipv6FirewallPolicySettings
        },
        {
          path: 'securityPolicy/firewall/ipv6FirewallAdSettings',
          name: 'policyManagement-securityPolicy-firewall-ipv6FirewallAdSettings',
          component: ipv6FirewallAdSettings
        },
        {
          path: 'securityPolicy/firewall/twoLayerDefense',
          name: 'policyManagement-securityPolicy-firewall-twoLayerDefense',
          component: twoLayerDefense
        },
        /** 보안 정책 - 블랙 리스트 */
        {
          path: 'securityPolicy/blackList/blackListReg',
          name: 'policyManagement-securityPolicy-blackList-blackListReg',
          component: blackListReg
        },
        {
          path: 'securityPolicy/blackList/blackListExtLinkage',
          name: 'policyManagement-securityPolicy-blackList-blackListExtLinkage',
          component: blackListExtLinkage
        },
        /** 보안 정책 - QoS */
        {
          path: 'securityPolicy/qos/qosPolicySettings',
          name: 'policyManagement-securityPolicy-qos-qosPolicySettings',
          component: qosPolicySettings
        },
        {
          path: 'securityPolicy/qos/qosProfileSettings',
          name: 'policyManagement-securityPolicy-qos-qosProfileSettings',
          component: qosProfileSettings
        },
        /** 네트워크 - 보호 도메인 */
        {
          path: 'securityPolicy/network/policyDomain',
          name: 'policyManagement-securityPolicy-network-policyDomain',
          component: protectionDomain
        },
        /** 네트워크 - 보호 도메인 */
        {
          path: 'securityPolicy/network/adsPolicyDomain',
          name: 'policyManagement-securityPolicy-network-adsPolicyDomain',
          component: adsPolicyDomain
        },
        /** 위협 정책 - 보안 센터 정책 */
        {
          path: 'securityPolicy/threatPolicy/securityCenterPolicy',
          name: 'policyManagement-securityPolicy-threatPolicy-securityCenterPolicy',
          component: securityCenterPolicy
        },
        /** 위협 정책 - Tams snort */
        {
          path: 'securityPolicy/threatPolicy/pop-snort-signature',
          name: 'policyManagement-securityPolicy-threatPolicy-pop-snort-signature',
          component: popSnortSignature
        },
        /** 위협 정책 - Tams yara **/
        {
          path: 'securityPolicy/threatPolicy/pop-yara-signature',
          name: 'policyManagement-securityPolicy-threatPolicy-pop-yara-signature',
          component: popYaraSignature
        },
        /** 객체 - 객체 */
        {
          path: 'objectSecProfile/object/objectSettings',
          name: 'policyManagement-objectSecProfile-object-objectSettings',
          component: objectSettings
        },
        {
          path: 'objectSecProfile/object/ipsObjectSettings',
          name: 'policyManagement-objectSecProfile-object-ipsObjectSettings',
          component: ipsObjectSettings
        },
        {
          path: 'objectSecProfile/object/objectMgmtSettings',
          name: 'policyManagement-objectSecProfile-object-objectMgmtSettings',
          component: objectMgmtSettings
        },
        /** 객체 - IPS */
        {
          path: 'objectSecProfile/ips/ipsProfileSettings',
          name: 'policyManagement-objectSecProfile-ips-ipsProfileSettings',
          component: ipsProfileSettings
        },
        {
          path: 'objectSecProfile/ips/ipsGeneralSettings',
          name: 'policyManagement-objectSecProfile-ips-ipsGeneralSettings',
          component: ipsGeneralSettings
        },
        {
          path: 'objectSecProfile/ips/ipsIDProfileSettings',
          name: 'policyManagement-objectSecProfile-ips-ipsIDProfileSettings',
          component: ipsIDProfileSettings
        },
        {
          path: 'objectSecProfile/ips/ipsProtectionProfileSettings',
          name: 'policyManagement-objectSecProfile-ips-ipsProtectionProfileSettings',
          component: ipsProtectionProfileSettings
        },
        {
          path: 'objectSecProfile/ips/ipsIDCommonPolicy',
          name: 'policyManagement-objectSecProfile-ips-ipsIDCommonPolicy',
          component: ipsIDCommonPolicy
        },
        /** 객체 - 안티 바이러스 */
        {
          path: 'objectSecProfile/antiVirus/antiVirusProfile',
          name: 'policyManagement-objectSecProfile-antiVirus-antiVirusProfile',
          component: antiVirusProfile
        },
        {
          path: 'objectSecProfile/antiVirus/checkExpList',
          name: 'policyManagement-objectSecProfile-antiVirus-checkExpList',
          component: checkExpList
        },
        /** 객체 - 안티 스팸 */
        {
          path: 'objectSecProfile/antiSpam/antiSpamProfile',
          name: 'policyManagement-objectSecProfile-antiSpam-antiSpamProfile',
          component: antiSpamProfile
        },
        {
          path: 'objectSecProfile/antiSpam/blockMailAddrList',
          name: 'policyManagement-objectSecProfile-antiSpam-blockMailAddrList',
          component: blockMailAddrList
        },
        {
          path: 'objectSecProfile/antiSpam/exceptionList',
          name: 'policyManagement-objectSecProfile-antiSpam-exceptionList',
          component: exceptionList
        },
        {
          path: 'objectSecProfile/antiSpam/forbiddenWordList',
          name: 'policyManagement-objectSecProfile-antiSpam-forbiddenWordList',
          component: forbiddenWordList
        },
        {
          path: 'objectSecProfile/antiSpam/protectMailServerList',
          name: 'policyManagement-objectSecProfile-antiSpam-protectMailServerList',
          component: protectMailServerList
        },
        {
          path: 'objectSecProfile/antiSpam/rblServerList',
          name: 'policyManagement-objectSecProfile-antiSpam-rblServerList',
          component: rblServerList
        },
        {
          path: 'objectSecProfile/antiSpam/smtpForbiddenCmdList',
          name: 'policyManagement-objectSecProfile-antiSpam-smtpForbiddenCmdList',
          component: smtpForbiddenCmdList
        },
        {
          path: 'objectSecProfile/antiSpam/spamChkServerList',
          name: 'policyManagement-objectSecProfile-antiSpam-spamChkServerList',
          component: spamChkServerList
        },
        /** 객체 - 웹 필터 */
        {
          path: 'objectSecProfile/webFilter/webFilterProfile',
          name: 'policyManagement-objectSecProfile-webFilter-webFilterProfile',
          component: webFilterProfile
        },
        {
          path: 'objectSecProfile/webFilter/anonymizerServerList',
          name: 'policyManagement-objectSecProfile-webFilter-anonymizerServerList',
          component: anonymizerServerList
        },
        /** 객체 - DLP */
        {
          path: 'objectSecProfile/dlp/dlpProfile',
          name: 'policyManagement-objectSecProfile-dlp-dlpProfile',
          component: dlpProfile
        },
        {
          path: 'objectSecProfile/dlp/dlpDataObject',
          name: 'policyManagement-objectSecProfile-dlp-dlpDataObject',
          component: dlpDataObject
        },
        {
          path: 'objectSecProfile/dlp/dlpDataGroupObject',
          name: 'policyManagement-objectSecProfile-dlp-dlpDataGroupObject',
          component: dlpDataGroupObject
        },
        {
          path: 'objectSecProfile/dlp/dlpExpProfile',
          name: 'policyManagement-objectSecProfile-dlp-dlpExpProfile',
          component: dlpExpProfile
        },
        /** 객체 - 파일 유형 제어 */
        {
          path: 'objectSecProfile/fileTypeControl/fileTypeCtrlProfile',
          name: 'policyManagement-objectSecProfile-fileTypeControl-fileTypeCtrlProfile',
          component: fileTypeCtrlProfile
        },
        {
          path: 'objectSecProfile/fileTypeControl/fileTypeCtrlExpProfile',
          name: 'policyManagement-objectSecProfile-fileTypeControl-fileTypeCtrlExpProfile',
          component: fileTypeCtrlExpProfile
        },
        /** 객체 - APT */
        {
          path: 'objectSecProfile/apt/aptCmnSettings',
          name: 'policyManagement-objectSecProfile-apt-aptCmnSettings',
          component: aptCmnSettings
        },
        {
          path: 'objectSecProfile/apt/aptProfile',
          name: 'policyManagement-objectSecProfile-apt-aptProfile',
          component: aptProfile
        },
        {
          path: 'objectSecProfile/apt/aptExceptProfile',
          name: 'policyManagement-objectSecProfile-apt-aptExceptProfile',
          component: aptExceptProfile
        },
        {
          path: 'objectSecProfile/apt/aptExceptObject',
          name: 'policyManagement-objectSecProfile-apt-aptExceptObject',
          component: aptExceptObject
        },
        /** VPN - IPSec VPN */
        {
          path: 'vpn/ipSecVpn/branchConnSettings',
          name: 'policyManagement-vpn-ipSecVpn-branchConnSettings',
          component: branchConnSettings
        },
        {
          path: 'vpn/ipSecVpn/pointReg',
          name: 'policyManagement-vpn-ipSecVpn-pointReg',
          component: pointReg
        },
        {
          path: 'vpn/ipSecVpn/secPolicySettings',
          name: 'policyManagement-vpn-ipSecVpn-secPolicySettings',
          component: secPolicySettings
        },
        {
          path: 'vpn/ipSecVpn/centerConnSettings',
          name: 'policyManagement-vpn-ipSecVpn-centerConnSettings',
          component: centerConnSettings
        },
        /** 로그/리포트 - 로그/리포트 관리 */
        {
          path: 'logReport/reportSettings/logMgmtSettings',
          name: 'policyManagement-logReport-reportSettings-logMgmtSettings',
          component: logMgmtSettings
        },
        {
          path: 'logReport/reportSettings/reportSettings',
          name: 'policyManagement-logReport-reportSettings-reportSettings',
          component: cmReportSettings
        },
        /** Device System - 시스템 구성 */
        {
          path: 'deviceSystem/systemConfig/licenseMgmt',
          name: 'policyManagement-deviceSystem-systemConfig-licenseMgmt',
          component: devLicensePatch
        },
        /** authentication 인증 서버 */
        {
          path: 'deviceSystem/authentication/authenticationServer',
          name: 'policyManagement-deviceSystem-authentication-authenticationServer',
          component: authenticationServer
        },
        /** Device System - 연동 서버 */
        {
          path: 'deviceSystem/linkageServer/mailServerSettings',
          name: 'policyManagement-deviceSystem-linkageServer-mailServerSettings',
          component: mailServerSettings
        },
        {
          path: 'deviceSystem/linkageServer/syslogSettings',
          name: 'policyManagement-deviceSystem-linkageServer-syslogSettings',
          component: syslogSettings
        },
        {
          path: 'deviceSystem/linkageServer/snmpSettings',
          name: 'policyManagement-deviceSystem-linkageServer-snmpSettings',
          component: snmpSettings
        },
        /** Device System - 경고 페이지 설정 ++ */
        {
          path: 'deviceSystem/warningPageSettings/warningPageSettings',
          name: 'policyManagement-deviceSystem-warningPageSettings-warningPageSettings',
          component: warningPageSettings
        },
        /** Device System - 관리자 설정 */
        {
          path: 'deviceSystem/adminSettings/administrator',
          name: 'policyManagement-deviceSystem-adminSettings-administrator',
          component: cmAdministrator
        },
        {
          path: 'deviceSystem/adminSettings/adminIpSettings',
          name: 'policyManagement-deviceSystem-adminSettings-adminIpSettings',
          component: cmAdminIpSettings
        },
        {
          path: 'deviceSystem/adminSettings/adminGeneralSettings',
          name: 'policyManagement-deviceSystem-adminSettings-adminGeneralSettings',
          component: cmAdminGeneralSettings
        },
        /** Device System - 설정 백업 스케쥴 */
        {
          path: 'deviceSystem/backupRestore/settingsBackupSchedule',
          name: 'policyManagement-deviceSystem-backupRestore-settingsBackupSchedule',
          component: settingsBackupSchedule
        },
        /** Device System - 설정 백업/복구 */
        {
          path: 'deviceSystem/backupRestore/backupRestore',
          name: 'policyManagement-deviceSystem-backupRestore-backupRestore',
          component: backupRestore
        },
        /** Device System - SW 업데이트 */
        {
          path: 'deviceSystem/softwareUpdate/softwareUpdates',
          name: 'policyManagement-deviceSystem-softwareUpdate-softwareUpdates',
          component: devSoftwarePatch
        },
        {
          path: 'deviceSystem/softwareUpdate/agentUpdates',
          name: 'policyManagement-deviceSystem-softwareUpdate-agentUpdates',
          component: devAgentPatch
        },
        /** Application DB 업데이트 applicationDbUpdates */
        {
          path: 'deviceSystem/softwareUpdate/applicationDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-applicationDB',
          component: devApplicationPatch
        },
        {
          path: 'deviceSystem/softwareUpdate/antiVirusDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-antiVirusDB',
          component: antiVirusDB
        },
        {
          path: 'deviceSystem/softwareUpdate/dlpDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-dlpDB',
          component: dlpDB
        },
        {
          path: 'deviceSystem/softwareUpdate/fileTypeCtrlDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-fileTypeCtrlDB',
          component: fileTypeCtrlDB
        },
        {
          path: 'deviceSystem/softwareUpdate/ipsSignatureDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-ipsSignatureDB',
          component: ipsSignatureDB
        },
        {
          path: 'deviceSystem/softwareUpdate/ngipsSignatureDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-ngipsSignatureDB',
          component: ngipsSignatureDB
        },
        {
          path: 'deviceSystem/softwareUpdate/adsIpsSignatureDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-adsIpsSignatureDB',
          component: adsIpsSignatureDB
        },
        {
          path: 'deviceSystem/softwareUpdate/aptPolicy',
          name: 'policyManagement-deviceSystem-softwareUpdate-aptPolicy',
          component: aptPolicy
        },
        {
          path: 'deviceSystem/softwareUpdate/webFilterDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-webFilterDB',
          component: webFilterDB
        },
        {
          path: 'deviceSystem/softwareUpdate/blackListDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-blackListDB',
          component: blackListDB
        },
        {
          path: 'deviceSystem/softwareUpdate/geoIpDB',
          name: 'policyManagement-deviceSystem-softwareUpdate-geoIpDB',
          component: geoIpDB
        },
        /** 통합 관리 - 통합 스크립트 */
        {
          path: 'deviceSystem/integrateScript/script',
          name: 'policyManagement-deviceSystem-integrateScript-script',
          component: integrateScript
        },
        /** 설정 동기화 - 설정 동기화 현황 ++ */
        {
          path: 'synchronization/syncStatus',
          name: 'policyManagement-synchronization-syncStatus',
          component: synchronizationStatus
        },
        /** 설정 동기화 - 동기화 설정 ++ */
        {
          path: 'synchronization/syncSettings',
          name: 'policyManagement-synchronization-syncSettings',
          component: syncSettings
        },
        /** SD-WAN - SD-WAN 기본 설정 - 경로 레이블 ++ */
        {
          path: 'sdwan/sdwanBasicSetting/sdwanPathProfile',
          name: 'policyManagement-sdwan-sdwanBasicSetting-sdwanPathProfile',
          component: sdwanPathProfile
        },
        /** SD-WAN - SD-WAN 기본 설정 - SD-WAN 기본 설정 프로파일 ++ */
        {
          path: 'sdwan/sdwanBasicSetting/sdwanDefaultPolicy',
          name: 'policyManagement-sdwan-sdwanBasicSetting-sdwanDefaultPolicy',
          component: sdwanDefaultPolicy
        },
        /** SD-WAN - 장비 설정 - 인터페이스 설정 */
        {
          path: 'sdwan/sdwanDeviceSetting/sdwanInterfaceSetting',
          name: 'policyManagement-sdwan-sdwanDeviceSetting-sdwanInterfaceSetting',
          component: sdwanInterfaceSetting
        },
        /** SD-WAN - 장비 설정 - 라우팅 설정 */
        {
          path: 'sdwan/sdwanDeviceSetting/sdwanRoutingSetting',
          name: 'policyManagement-sdwan-sdwanDeviceSetting-sdwanRoutingSetting',
          component: sdwanRoutingSetting
        },
        /** SD-WAN 설정 - SD-WAN 정책 설정 */
        {
          path: 'sdwan/sdwanSetting/sdwanPolicySetting',
          name: 'policyManagement-sdwan-sdwanSetting-sdwanPolicySetting',
          component: sdwanPolicySetting
        },
        /** SD-WAN 설정 - SD-WAN 프로파일 설정 */
        {
          path: 'sdwan/sdwanSetting/sdwanProfileSetting',
          name: 'policyManagement-sdwan-sdwanSetting-sdwanProfileSetting',
          component: sdwanProfileSetting
        },
        /** SD-WAN - 터널 설정 - 터널 그룹 설정 */
        {
          path: 'sdwan/sdwanTunnelSetting/sdwanTunnelGroupSetting',
          name: 'policyManagement-sdwan-sdwanTunnelSetting-sdwanTunnelGroupSetting',
          component: sdwanTunnelGroupSetting
        }
      ]
    },

    /** PAMS */
    {
      path: '/policyAnalysis',
      name: 'policyAnalysis',
      component: policyAnalysis,
      children: [
        /** 기본 설정 */
        {
          path: 'settings/useFirewall',
          name: 'policyAnalysis-settings-useFirewall',
          component: useFirewall
        },
        {
          path: 'settings/internalNetwork',
          name: 'policyAnalysis-settings-internalNetwork',
          component: internalNetwork
        },
        {
          path: 'settings/forbidNetwork',
          name: 'policyAnalysis-settings-forbidNetwork',
          component: forbidNetwork
        },
        {
          path: 'settings/overAllowPolicy',
          name: 'policyAnalysis-settings-overAllowPolicy',
          component: overAllowPolicy
        },
        /** 정책 분석 */
        {
          path: 'analysis/ruleObjectOptimize',
          name: 'policyAnalysis-analysis-ruleObjectOptimize',
          component: ruleObjectOptimize
        },
        {
          path: 'analysis/ruleComplianceDetect',
          name: 'policyAnalysis-analysis-ruleComplianceDetect',
          component: ruleComplianceDetect
        },
        {
          path: 'analysis/ruleObjectTrafficAnalysis',
          name: 'policyAnalysis-analysis-ruleObjectTrafficAnalysis',
          component: ruleObjectTrafficAnalysis
        },
        /** 정책 신청 관리 */
        {
          path: 'pams/requestRuleProcess',
          name: 'policyAnalysis-pams-requestRuleProcess',
          component: requestRuleProcess
        },
        {
          path: 'pams/resultInquiry',
          name: 'policyAnalysis-pams-resultInquiry',
          component: resultInquiry
        },
        {
          path: 'pams/firewallMatchRule',
          name: 'policyAnalysis-pams-firewallMatchRule',
          component: firewallMatchRule
        },
        {
          path: 'pams/firewallZoneSettings',
          name: 'policyAnalysis-pams-firewallZoneSettings',
          component: firewallZoneSettings
        },
        {
          path: 'pams/generalSettings',
          name: 'policyAnalysis-pams-generalSettings',
          component: generalSettings
        }
      ]
    },

    /** System */
    {
      path: '/prismSystem',
      name: 'prismSystem',
      component: systemParent,
      children: [
        /** 장비 관리 */
        {
          path: 'deviceConfig/deviceCompositionStatus',
          name: 'prismSystem-deviceConfig-deviceCompositionStatus',
          component: deviceCompositionStatus
        },
        {
          path: 'deviceConfig/deviceGroupSettings',
          name: 'prismSystem-deviceConfig-deviceGroupSettings',
          component: deviceGroupSettings
        },
        {
          path: 'deviceConfig/deviceSearch',
          name: 'prismSystem-deviceConfig-deviceSearch',
          component: deviceSearch
        },
        /** ZTP 토큰 */
        {
          path: 'deviceConfig/ZtpToken',
          name: 'prismSystem-deviceConfig-ZtpToken',
          component: deviceZtpToken
        },
        {
          path: 'deviceConfig/wipsDeviceLink',
          name: 'prismSystem-deviceConfig-wipsDeviceLink',
          component: wipsDeviceLink
        },
        /** 관리자 설정 */
        {
          path: 'adminSettings/adminGeneralSettings',
          name: 'prismSystem-adminSettings-adminGeneralSettings',
          component: adminGeneralSettings
        },
        {
          path: 'adminSettings/administrator',
          name: 'prismSystem-adminSettings-administrator',
          component: administrator
        },
        {
          path: 'adminSettings/adminGroupSettings',
          name: 'prismSystem-adminSettings-adminGroupSettings',
          component: adminGroupSettings
        },
        {
          path: 'adminSettings/adminIpSettings',
          name: 'prismSystem-adminSettings-adminIpSettings',
          component: adminIpSettings
        },
        {
          path: 'adminSettings/adminDeviceGroupSettings',
          name: 'prismSystem-adminSettings-adminDeviceGroupSettings',
          component: adminDeviceGroupSettings
        },
        {
          path: 'adminSettings/externalAuthSettings',
          name: 'prismSystem-adminSettings-externalAuthSettings',
          component: externalAuthSettings
        },
        /** 모니터링 설정 */
        {
          path: 'monitoringSettings/eventGeneralSettings',
          name: 'prismSystem-monitoringSettings-eventGeneralSettings',
          component: eventGeneralSettings
        },
        {
          path: 'monitoringSettings/eventRuleSettings',
          name: 'prismSystem-monitoringSettings-eventRuleSettings',
          component: eventRuleSettings
        },
        /* Ticket Menu setting - by wj_tams_dev01 */
        {
          path: 'monitoringSettings/ticketMgmt/ticketSettings',
          name: 'prismSystem-monitoringSettings-ticketMgmt-ticketSettings',
          component: ticketSettings
        },
        {
          path: 'monitoringSettings/ticketMgmt/ticketGroupSettings',
          name: 'prismSystem-monitoringSettings-ticketMgmt-ticketGroupSettings',
          component: ticketGroupSettings
        },
        /** 외부 연동 서버 */
        {
          path: 'externalServer/smtpSettings',
          name: 'prismSystem-externalServer-smtpSettings',
          component: smtpSettings
        },
        {
          path: 'externalServer/signatureSettings',
          name: 'prismSystem-externalServer-signatureSettings',
          component: signatureSettings
        },
        {
          path: 'externalServer/signatureDetails',
          name: 'prismSystem-externalServer-signatureDetails',
          component: signatureDetails
        },
        {
          path: 'externalServer/systemSnmpSettings',
          name: 'prismSystem-externalServer-systemSnmpSettings',
          component: systemSnmpSettings
        },
        {
          path: 'externalServer/smsSettings',
          name: 'prismSystem-externalServer-smsSettings',
          component: smsSettings
        },
        {
          path: 'externalServer/tamsSyslogSettings',
          name: 'prismSystem-externalServer-tamsSyslogSettings',
          component: tamsSyslogSettings
        },
        {
          path: 'externalServer/tamsLogSendSettings',
          name: 'prismSystem-externalServer-tamsLogSendSettings',
          component: tamsLogSendSettings
        },
        /** 시스템 구성 */
        {
          path: 'systemConfig/systemSettings',
          name: 'prismSystem-systemConfig-systemSettings',
          component: systemSettings
        },
        {
          path: 'systemConfig/licenseMgmt',
          name: 'prismSystem-systemConfig-licenseMgmt',
          component: licenseMgmt
        },
        {
          path: 'systemConfig/mfxMgmt',
          name: 'prismSystem-systemConfig-mfxMgmt',
          component: dockerMgmt
        },
        {
          path: 'systemConfig/systemBackupSchedule',
          name: 'prismSystem-systemConfig-systemBackupSchedule',
          component: systemBackupSchedule
        },
        {
          path: 'systemConfig/systemBackup',
          name: 'prismSystem-systemConfig-systemBackup',
          component: systemBackup
        },
        {
          path: 'systemConfig/networkSettings',
          name: 'prismSystem-systemConfig-networkSettings',
          component: networkSettings
        },
        {
          path: 'systemConfig/dnsSettings',
          name: 'prismSystem-systemConfig-dnsSettings',
          component: dnsSettings
        },
        {
          path: 'systemConfig/clusterSettings',
          name: 'prismSystem-systemConfig-clusterSettings',
          component: clusterSettings
        },
        /** 패치 관리 ++ */
        {
          path: 'patch/tamsPatch',
          name: 'prismSystem-patch-tamsPatch',
          component: prismPatch
        },
        /** Tools */
        {
          path: 'tools/ping',
          name: 'prismSystem-tools-ping',
          component: ping
        },
        {
          path: 'tools/traceRoute',
          name: 'prismSystem-tools-traceRoute',
          component: traceRoute
        },
        {
          path: 'tools/whois',
          name: 'prismSystem-tools-whois',
          component: whois
        },
        {
          path: 'tools/snmp',
          name: 'prismSystem-tools-snmp',
          component: snmp
        },
        {
          path: 'tools/terminalAccess',
          name: 'prismSystem-tools-terminalAccess',
          component: terminalAccess
        },
        {
          path: 'tools/integrityCheck',
          name: 'prismSystem-tools-integrityCheck',
          component: integrityCheck
        },

        /** 시스템 종료/재시작 */
        {
          path: 'systemReStart/systemReStart',
          name: 'prismSystem-systemReStart-systemReStart',
          component: systemReStart
        }
      ]
    }
  ],

  fallback: false
}

export function createRouter (ssrContext, _createDefaultRouter, _routerOptions, config) {
  const base = (config && config._app && config._app.basePath) || routerOptions.base
  const router = new Router({ ...routerOptions, base  })

  // TODO: remove in Nuxt 3
  const originalPush = router.push
  router.push = function push (location, onComplete = emptyFn, onAbort) {
    return originalPush.call(this, location, onComplete, onAbort)
  }

  const resolve = router.resolve.bind(router)
  router.resolve = (to, current, append) => {
    if (typeof to === 'string') {
      to = normalizeURL(to)
    }
    return resolve(to, current, append)
  }

  return router
}

